/**
 * Reading puns, in one place.
 *
 * Firestore access used to live inline in `PunFeed`, `UserPuns` and `Login`,
 * three copies that had already drifted. This is the data layer CLAUDE.md lists
 * as planned.
 *
 * Two deliberate choices:
 *
 *   - No `orderBy` anywhere. Sorting happens on the client, because equality
 *     plus orderBy on a different field needs a composite index per query shape
 *     and the payoff is nothing: a day's feed holds at most one pun per player,
 *     and a single player's history grows by one a day.
 *   - Friend feeds are split into chunks of 30. `where(..., 'in', ...)` caps at
 *     30 values, so the old single query silently broke for anyone with more
 *     than 30 friends (gotcha 6).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { collection, doc, getDoc, onSnapshot, query, where } from 'firebase/firestore';

import { firestore } from '../firebase';
import { pacificDayString } from '../../shared/dates';
import { displayNameOf, type Pun, type Reaction } from '../../shared/schemas';
import { punsFromSnapshot, sortPunsByCrowns } from './puns';
import { userFromDoc } from './users';
import { ApiError, setReaction } from './api';

/** Firestore's hard cap on values in an `in` filter. */
const IN_FILTER_LIMIT = 30;

export type FeedScope = 'everyone' | 'friends' | 'user';

/**
 * Which game days a feed covers.
 *
 * `since` is inclusive. It is normally open-ended on the right, which is fine
 * because no pun can carry a `pstDay` in the future — the server stamps it.
 * `through` closes it, also inclusive, for the one caller that needs a window
 * ending before today: the feed browsed by someone who has not posted yet.
 */
export type FeedRange =
  | { kind: 'day'; day: string }
  | { kind: 'since'; since: string; through?: string }
  | { kind: 'all' };

export interface FeedOptions {
  scope: FeedScope;
  viewerId: string;
  /** Whose puns, when scope is 'user'. */
  targetUserId?: string;
  range: FeedRange;
  /**
   * Crowned first instead of newest first. Wanted for settled days, where the
   * interesting thing is which pun won rather than who posted last.
   */
  rankByCrowns?: boolean;
}

export interface FeedResult {
  puns: Pun[];
  /** Display names by user id, filled in as lookups land. */
  names: Record<string, string>;
  loading: boolean;
  error: string | null;
  dismissError: () => void;
  /** True when scope is 'friends' and the viewer follows nobody. */
  noFriends: boolean;
  /** The viewer has already spent today's crown on a pun in this feed. */
  crownSpent: boolean;
  react: (pun: Pun, reaction: Reaction) => Promise<void>;
}

function chunk<T>(items: T[], size: number): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < items.length; i += size) out.push(items.slice(i, i + size));
  return out;
}

export function usePunFeed({
  scope,
  viewerId,
  targetUserId,
  range,
  rankByCrowns = false,
}: FeedOptions): FeedResult {
  // Destructured so the effect depends on primitives rather than on an object
  // literal, which would be a fresh reference on every render and resubscribe
  // the listener each time.
  const rangeKind = range.kind;
  const rangeDay = range.kind === 'day' ? range.day : range.kind === 'since' ? range.since : '';
  const rangeThrough = range.kind === 'since' ? (range.through ?? '') : '';

  const [puns, setPuns] = useState<Pun[]>([]);
  const [names, setNames] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [noFriends, setNoFriends] = useState(false);

  /** Ids already looked up, so each name is fetched once. */
  const requestedNames = useRef<Set<string>>(new Set());

  useEffect(() => {
    let cancelled = false;
    const unsubscribers: Array<() => void> = [];

    setLoading(true);
    setNoFriends(false);

    const start = async () => {
      // Each chunk's latest snapshot, merged on every update. A plain array
      // would let one chunk's results overwrite another's.
      const byChunk = new Map<number, Pun[]>();

      const publish = () => {
        if (cancelled) return;

        const merged = [...byChunk.values()].flat();
        setPuns(
          rankByCrowns
            ? sortPunsByCrowns(merged)
            : merged.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime()),
        );
        setLoading(false);
      };

      const listen = (index: number, filters: ReturnType<typeof where>[]) => {
        const unsubscribe = onSnapshot(
          query(collection(firestore, 'puns'), ...filters),
          (snapshot) => {
            byChunk.set(index, punsFromSnapshot(snapshot, 'usePunFeed'));
            publish();
          },
          (err) => {
            console.error('[usePunFeed] listener failed:', err);
            if (cancelled) return;
            setLoading(false);
            setError(
              navigator.onLine
                ? 'Could not load puns. Refreshing usually sorts it out.'
                : 'You are offline. Puns will load when you reconnect.',
            );
          },
        );

        unsubscribers.push(unsubscribe);
      };

      /*
       * A range on `pstDay` rather than on `createdAt`.
       *
       * `pstDay` is the day the server decided a pun belongs to, so it matches
       * what the rest of the game means by "yesterday". `createdAt` is an
       * instant, and bucketing instants into days is what produced the
       * timezone bugs in gotcha 1.
       */
      const dayFilter =
        rangeKind === 'day'
          ? [where('pstDay', '==', rangeDay)]
          : rangeKind === 'since'
            ? [
                where('pstDay', '>=', rangeDay),
                // Both bounds sit on `pstDay`, so this needs no index the
                // open-ended version did not already need.
                ...(rangeThrough ? [where('pstDay', '<=', rangeThrough)] : []),
              ]
            : [];

      if (scope === 'everyone') {
        listen(0, dayFilter);
        return;
      }

      if (scope === 'user') {
        if (!targetUserId) {
          setLoading(false);
          return;
        }
        listen(0, [where('userId', '==', targetUserId), ...dayFilter]);
        return;
      }

      // Friends. The follow list has to be read before the puns can be queried.
      try {
        const snapshot = await getDoc(doc(firestore, 'users', viewerId));
        const friends = userFromDoc(snapshot, 'usePunFeed')?.friends ?? [];

        if (cancelled) return;

        if (friends.length === 0) {
          setNoFriends(true);
          setPuns([]);
          setLoading(false);
          return;
        }

        chunk(friends, IN_FILTER_LIMIT).forEach((group, index) => {
          listen(index, [where('userId', 'in', group), ...dayFilter]);
        });
      } catch (err) {
        console.error('[usePunFeed] could not read follow list:', err);
        if (!cancelled) {
          setError('Could not load your friends list.');
          setLoading(false);
        }
      }
    };

    start();

    return () => {
      cancelled = true;
      unsubscribers.forEach((unsubscribe) => unsubscribe());
    };
  }, [scope, viewerId, targetUserId, rangeKind, rangeDay, rangeThrough, rankByCrowns]);

  // Resolve author names for whatever is on screen, once each, concurrently.
  useEffect(() => {
    const missing = [...new Set(puns.map((pun) => pun.userId))].filter(
      (id) => !requestedNames.current.has(id),
    );
    if (missing.length === 0) return;

    missing.forEach((id) => requestedNames.current.add(id));
    let cancelled = false;

    Promise.all(
      missing.map(async (id) => {
        const user = userFromDoc(await getDoc(doc(firestore, 'users', id)), 'usePunFeed');
        return [id, user ? displayNameOf(user) : 'Anonymous'] as const;
      }),
    )
      .then((entries) => {
        if (!cancelled) setNames((prev) => ({ ...prev, ...Object.fromEntries(entries) }));
      })
      .catch((err) => {
        console.error('[usePunFeed] name lookup failed:', err);
        // Allow a retry on the next render.
        missing.forEach((id) => requestedNames.current.delete(id));
      });

    return () => {
      cancelled = true;
    };
  }, [puns]);

  /*
   * Whether the viewer's crown is spent, as far as this feed can tell.
   *
   * Only counts puns actually loaded, so on a friends feed it can be wrong: a
   * crown given to someone you do not follow will not appear here. That is
   * cosmetic. The real limit is a `dailyCrowns/{uid}_{day}` document written in
   * the same transaction as the reaction, and the server returns
   * resource-exhausted regardless of what the client believed.
   */
  const today = pacificDayString();
  const crownSpent = useMemo(
    () => puns.some((pun) => pun.pstDay === today && pun.reactions[viewerId]?.[0] === 'crown'),
    [puns, viewerId, today],
  );

  const react = useCallback(
    async (pun: Pun, reaction: Reaction) => {
      if (pun.userId === viewerId) return;

      const previous = pun.reactions;
      // Tapping the current reaction again clears it, matching the server.
      const next = previous[viewerId]?.[0] === reaction ? null : reaction;

      const optimistic: Record<string, Reaction[]> = { ...previous };
      if (next === null) delete optimistic[viewerId];
      else optimistic[viewerId] = [next];

      setPuns((current) =>
        current.map((item) => (item.id === pun.id ? { ...item, reactions: optimistic } : item)),
      );

      try {
        await setReaction(pun.id, next);
        setError(null);
      } catch (err) {
        console.error('[usePunFeed] reaction failed:', err);
        setError(err instanceof ApiError ? err.message : 'Could not save that reaction.');
        setPuns((current) =>
          current.map((item) => (item.id === pun.id ? { ...item, reactions: previous } : item)),
        );
      }
    },
    [viewerId],
  );

  const dismissError = useCallback(() => setError(null), []);

  return { puns, names, loading, error, dismissError, noFriends, crownSpent, react };
}
